import { useMemo } from "react";
import type { WorkPlan } from "../../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import { appTime, appWeekdayTime } from "../../src/app-dates.ts";
import type { EmailDisplay } from "../../src/email-display.ts";
import type { DraftRevision, ThreadSnapshot } from "../../src/mail-content.ts";
import { prepareMailDisplay } from "../mail/mail-images.ts";

/*
 * What both halves of a conversation use: the thread (`Conversation`, `main.tsx`) and the work pane
 * (`work-pane.tsx`). Moved verbatim from `main.tsx` in step 8b of
 * docs/2026-10-17-master-inbox-in-the-existing-ui-plan.md, so neither half imports the other for them.
 */
export const dispositionLabel: Record<WorkPlan["disposition"], string> = {
  active: "Active",
  qualification_pending: "Qualification pending",
  qualification_error: "Qualification error",
  ignored_not_qualified: "Not qualified · no draft",
  drafting_off: "Drafting off",
  back_drafting_disabled: "Imported history · drafting unavailable",
  backfill_not_drafted: "Imported history · not drafted",
  sender_only_cc: "You were only Cc'd · no draft",
  answered_by_seller: "Answered in Bison · no draft",
  meeting_already_confirmed: "Meeting already booked · no draft",
  continued_not_drafted: "Wrote again outside the campaign · no draft",
};

/** Why a held conversation has no draft, as its sheet says; `drafting_off` names its switch. */
export const heldNote: Record<
  Exclude<WorkPlan["disposition"], "active" | "drafting_off">,
  string
> = {
  qualification_pending:
    "This reply is visible while the external qualification result is pending. Drafting has not started.",
  qualification_error:
    "The qualification result reported an error. Drafting remains paused until an explicit result arrives.",
  ignored_not_qualified:
    "The external workflow explicitly marked this reply not qualified. No draft was created.",
  back_drafting_disabled:
    "This reply came in with imported history. Imported-history drafting is not included for this workspace, so no draft is prepared.",
  backfill_not_drafted:
    "This reply came in with the campaign's imported history and is older than the drafting limit, so no draft is prepared.",
  sender_only_cc:
    "The lead wrote to someone else and copied your mailbox. Handle it yourself if it needs an answer; no draft is prepared.",
  answered_by_seller:
    "This reply was already answered in Bison before LeadCue imported it, so no draft is prepared.",
  meeting_already_confirmed:
    "This lead already has a meeting booked, in this conversation or another one from the same campaign. The reply is shown so you can answer it yourself; no draft with new times is prepared.",
  continued_not_drafted:
    "The lead, or someone they brought in, wrote again after you answered in Bison, and Bison filed it outside the campaign. It is shown so you can answer it yourself; no draft is prepared.",
};

/** The holds whose sheet offers the qualification actions: the qualification holds themselves. */
export const qualifiable: ReadonlySet<WorkPlan["disposition"]> = new Set([
  "qualification_pending",
  "qualification_error",
  "ignored_not_qualified",
]);

// Resolved at the first parse, not at import: a module loaded before a DOM exists would otherwise
// keep a null parser for good (a test process that loads it early, as a CI shard did).
let parser: DOMParser | null = null;
export const parseEmail = (html: string): Document => {
  if (!parser && typeof DOMParser !== "undefined") parser = new DOMParser();
  if (!parser) throw new Error("Email display needs a DOM");
  return parser.parseFromString(html, "text/html");
};

/** Asks the open conversation to show one message, opening the earlier-messages fold if needed. */
export const SHOW_MESSAGE_EVENT = "lcr-show-message";

export function whenLabel(at: string): string {
  const date = new Date(at);
  const today = new Date().toDateString() === date.toDateString();
  return today ? `today ${appTime(at)}` : appWeekdayTime(at);
}

/** Each message prepared once per thread: its quoted history folded against the messages before it. */
export function useEmailDisplays(thread: ThreadSnapshot | null): EmailDisplay[] {
  return useMemo(
    () =>
      thread?.messages.map((message, index) =>
        prepareMailDisplay(message.html, thread.messages.slice(0, index), parseEmail),
      ) ?? [],
    [thread],
  );
}

/**
 * The newest draft the pipeline wrote for this conversation, or null where it wrote none: what the
 * reply box may start from (the reply agent's compose box and the master inbox's reply box alike).
 */
export function newestPipelineDraft(
  plan: WorkPlan,
  drafts: readonly DraftRevision[],
): DraftRevision | null {
  const newest = plan.steps.findLast((step) => step.input.kind === "reply")?.input;
  if (newest?.kind !== "reply") return null;
  return drafts.find((draft) => draft.revision === newest.draftRevision) ?? null;
}
